import { DestroyRef, inject, Injectable, signal, computed, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadingState } from '@app/shared/interfaces';
import { DYNAMIC_PRICING_ENABLE_PERMISSIONS, DynamicPricingOptionsResponse } from '@app/shared/interfaces/lib/dynamic-pricing.interface';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { PusherService } from '@app/shared/services/pusher/pusher.service';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { BehaviorSubject, combineLatest, filter, map, Observable, of, startWith, switchMap, take, tap } from 'rxjs';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { UsersFacade } from '@app/shared/+state/users';
import { BillingFacade, PlanId } from '@app/shared/+state/billing';
import { EventsService } from '@app/shared/services/events/events.service';

interface DynamicPricingServiceState {
  $config: WritableSignal<DynamicPricingOptionsResponse | null>;
  $configLoading: WritableSignal<LoadingState>;
  $propertyId: WritableSignal<number | null>;
}

type StateKey = keyof DynamicPricingServiceState;

@Injectable({
  providedIn: 'root',
})
export class DynamicPricingService {
  // Dependencies
  private pusherService: PusherService = inject(PusherService);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private posthogService: PosthogService = inject(PosthogService);
  private propertiesService: PropertiesService = inject(PropertiesService);

  // State signals
  private state: DynamicPricingServiceState = {
    $config: signal<DynamicPricingOptionsResponse | null>(null),
    $configLoading: signal(LoadingState.NotSent),
    $propertyId: signal<number | null>(null),
  };

  // Selectors
  public $config = computed(() => this.state.$config());
  public $dynamicPricingOn = computed(() => this.state.$config()?.outbound_dynamic_pricing_enabled);
  public $dynamicPricingErrors = computed(() => this.state.$config()?.data_errors);
  public $dynamicPricingOutOfMarket = computed(() => !this.state.$config()?.in_market);
  public $configLoading = computed(() => this.state.$configLoading());
  public $propertyId = computed(() => this.state.$propertyId());

  // Observables for state
  public dynamicPricingEnabled$ = this.posthogService.dynamicPricingEnabled$;
  private enableOutboundDynamicPricingLoading$ = new BehaviorSubject<LoadingState>(LoadingState.NotSent);
  public dynamicPricingPusherUpdates$ = new BehaviorSubject<boolean>(false);
  public enableDynamicPricingProgress$ = this.enableOutboundDynamicPricingLoading$.asObservable().pipe(
    switchMap((loading) => {
      if (loading !== LoadingState.Success) {
        return of(1);
      }

      return this.dynamicPricingPusherUpdates$.pipe(
        filter((updated) => updated),
        take(1),
        map(() => 2)
      );
    })
  );


  // Observables for feature permissions
  private plan$ = this.usersFacade.currentPlanId$;
  private isTrial$ = this.billingFacade.isTrialSubscription$;
  private subscription$ = this.billingFacade.subscription$;
  private isPrimaryUser = this.permissionsService.isPrimaryUser();
  private embeddedPricingEntitlement$ = this.usersFacade.embeddedPricingEntitlement$.pipe(
    map((entitlement) => {
      return entitlement?.available;
    })
  );

  public dynamicPricingEnablePermissions$: Observable<DYNAMIC_PRICING_ENABLE_PERMISSIONS> = combineLatest([this.plan$, this.subscription$, this.isTrial$, this.embeddedPricingEntitlement$, this.isPrimaryUser])
    .pipe(
      map(([plan, subscription, isTrial, embeddedPricingEntitlement, isPrimaryUser]) => {
        let permission;
        if (!isPrimaryUser) {
          permission = DYNAMIC_PRICING_ENABLE_PERMISSIONS.ADMIN_ACCOUNT_REQUIRED
        }
        else if (isTrial) {
          permission = DYNAMIC_PRICING_ENABLE_PERMISSIONS.BILLING_UPDATE_REQUIRED;
        }
        else if (plan === PlanId.Legacy && !embeddedPricingEntitlement) {
          permission = DYNAMIC_PRICING_ENABLE_PERMISSIONS.PLAN_UPGRADE_REQUIRED;
        }
        // primary user or legacy w/ embeddedPricingEntitlement AND subscription has been loaded
        else if (subscription) {
          permission = DYNAMIC_PRICING_ENABLE_PERMISSIONS.AVAILABLE;
        }
        return permission;
      }),
      filter((permission) => permission !== undefined)
    );

  public dynamicPricingSidebarSubtitle$: Observable<string> = this.dynamicPricingEnablePermissions$.pipe(
    map((permissions) => {
      return permissions === DYNAMIC_PRICING_ENABLE_PERMISSIONS.AVAILABLE ? 'Included in your plan' : 'Included with Host, Pro and Mogul plans';
    }),
    startWith('Included with Host, Pro and Mogul plans')
  );

  /**
   * Initialize the dynamic pricing service for a property
   * @param propertyId - The ID of the property to initialize the service for
   */
  public init(propertyId: number) {
    this.resetState();
    this.updateState('$propertyId', propertyId);
    this.loadDynamicPricingConfig();
    this.pusherService
      .getDynamicPricingRecommendationsUpdate(propertyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadDynamicPricingConfig();
        this.dynamicPricingPusherUpdates$.next(true);
      });
    this.isPrimaryUser.pipe(
      filter((isPrimaryUser) => isPrimaryUser),
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.billingFacade.loadSubscription();
    });
  }

  public resetState() {
    this.updateState('$propertyId', null);
    this.updateState('$configLoading', LoadingState.NotSent);
    this.updateState('$config', null);
  }

  public loadDynamicPricingConfig() {
    this.updateState('$configLoading', LoadingState.Pending);
    this.propertiesService.getDynamicPricingOptions(this.$propertyId()).subscribe((data) => {
      this.updateState('$config', data as DynamicPricingOptionsResponse);
      this.updateState('$configLoading', LoadingState.Success);
    });
  }

  // TODO: this just updates the state for now
  public setDynamicPricingConfig(payload: DynamicPricingOptionsResponse) {
    this.updateState('$config', payload);
  }

  public activateOutboundDynamicPricing() {
    this.enableOutboundDynamicPricingLoading$.next(LoadingState.Pending);
  }

  public activateOutboundDynamicPricingSuccess() {
    this.enableOutboundDynamicPricingLoading$.next(LoadingState.Success);
  }

  /**
   * Updates a specific piece of state
   * @param key - The state key to update
   * @param value - The new value
   */
  private updateState(key: StateKey, value: unknown) {
    const signal = this.state[key];
    if (key === '$config' && value !== null) {
      const currentValue = signal() || {};
      // @ts-expect-error - Dynamic typing of value is tricky here
      signal.set({ ...currentValue, ...value });
    } else {
      signal.set(value as never);
    }
  }

  public clearEvents(propertyId: number) {
    // clear navigation-related events, attached full list though
    this.eventsService.clearEvents([
      // `dynamic-pricing-disabled-${propertyId}`,
      // `dynamic-pricing-enabled-${propertyId}`,
      `dynamic-pricing-preview-fetch-${propertyId}`,
      // `dynamic-pricing-preview-loading-${propertyId}`,
      // `dynamic-pricing-property-error-modal-${propertyId}`,
      `dynamic-pricing-sidebar-view-force-${propertyId}`,
      // `dynamic-pricing-updated-${propertyId}`,
      // `dynamic-pricing-visible-change-${propertyId}`
    ]);
  }

  constructor(
    private usersFacade: UsersFacade,
    private permissionsService: UserPermissionsService,
    private billingFacade: BillingFacade,
    private eventsService: EventsService
  ) {}
}
